import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Download, Filter, Search } from 'lucide-react';
import { downloadCsv } from './format';
import { Reveal } from '../../../lib/motion';

export interface Column<T> {
  key: string;
  header: string;
  /** Cell content. Falls back to `value` if omitted. */
  render?: (row: T) => ReactNode;
  /** Raw value — used for sorting, search and CSV export. */
  value?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  /** Narrow column: hugs its content instead of stretching. Default true for non-`grow` columns. */
  grow?: boolean;
  sortable?: boolean;
  /** Extra classes on the <td>. */
  cellClass?: (row: T) => string;
  /** Hide from CSV export. */
  noExport?: boolean;
  /** Opt this column out of the header filter (when `columnFilters` is on). */
  filterable?: boolean;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Array<Column<T>>;
  rowKey: (row: T, i: number) => string;
  /** Renders under the row when expanded. Omit to disable expansion. */
  expand?: (row: T) => ReactNode;
  /**
   * Row to start expanded, as its `rowKey`. For deep links — Master Search
   * lands on a table with one row already in mind, and the table owns the
   * open-row state, so without this the link can only scroll to the page.
   */
  initialOpenKey?: string | null;
  onRowClick?: (row: T) => void;
  /** Show the search box. Searches every column's `value`. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Extra controls rendered in the toolbar (chips, selects). */
  toolbar?: ReactNode;
  /** Enables the CSV button. */
  exportName?: string;
  emptyText?: string;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Rows per page. 0 = show all. */
  pageSize?: number;
  /** Sticky header offset in px (below a sticky toolbar). */
  maxHeight?: string;
  dense?: boolean;
  caption?: ReactNode;
  /**
   * Column keys to total in a footer row.
   *
   * Sums the WHOLE filtered set, not the visible page — a total that changed
   * when you turned the page would be worse than no total at all.
   */
  totalKeys?: string[];
  /** How to render each total. Defaults to a plain locale number. */
  formatTotal?: (key: string, value: number) => ReactNode;
  /**
   * Collapse columns that carry no information for the CURRENT rows — every
   * cell blank, or every cell the same value. On real data this is most of the
   * clutter: the vendor grid was showing a Bank column that was empty on all
   * 148 rows and an Active column reading "Yes" 148 times, and the finance
   * grid gave three of its seven columns to constants.
   *
   * Nothing is destroyed — a chip above the table says what was folded away
   * and puts it back on one click, and CSV export always includes every
   * column. Set false where a column must stay visible even when constant
   * (a compliance grid where "all blank" is itself the finding).
   */
  hideDeadColumns?: boolean;
  /** Rows that stay above the rest whatever the sort (e.g. parts marked duplicate). */
  pinTop?: (row: T) => boolean;
  /**
   * Excel-style filter in every column header: a list of the column's unique
   * values with checkboxes, several can be ticked. Lists are cascading — each
   * offers only the values left by the other columns' filters.
   */
  columnFilters?: boolean;
  /** Called with the rows left after search + column filters (for export / select-all). */
  onFilteredRowsChange?: (rows: T[]) => void;
}

const BLANK = '(blank)';
const filterLabel = (v: unknown) => {
  const t = String(v ?? '').trim();
  return t === '' || t === '—' ? BLANK : t;
};

type Dir = 'asc' | 'desc';

/**
 * Excel-like table: grid lines on every cell, sticky shaded header, click-to-sort,
 * zebra rows, fans out to the full width. Long columns stretch; short ones hug.
 */
export default function DataTable<T>({
  rows,
  columns,
  rowKey,
  expand,
  initialOpenKey = null,
  onRowClick,
  searchable = true,
  searchPlaceholder = 'Search…',
  toolbar,
  exportName,
  emptyText = 'Nothing to show',
  initialSort,
  pageSize = 100,
  maxHeight,
  dense,
  caption,
  totalKeys,
  formatTotal,
  hideDeadColumns = true,
  pinTop,
  columnFilters = false,
  onFilteredRowsChange,
}: DataTableProps<T>) {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(initialSort?.key ?? null);
  const [sortDir, setSortDir] = useState<Dir>(initialSort?.dir ?? 'asc');
  const [open, setOpen] = useState<string | null>(initialOpenKey);
  const [page, setPage] = useState(0);
  const [showDead, setShowDead] = useState(false);
  /** column key → the values ticked in its header filter. Absent = no filter. */
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  const [filterOpen, setFilterOpen] = useState<{ key: string; x: number; y: number } | null>(null);

  const valueOf = (row: T, col: Column<T>) => (col.value ? col.value(row) : undefined);

  const passes = (r: T, skipKey?: string) => {
    for (const [k, set] of Object.entries(colFilters)) {
      if (k === skipKey) continue;
      const col = columns.find((c) => c.key === k);
      if (col && !set.has(filterLabel(valueOf(r, col)))) return false;
    }
    return true;
  };
  const colFiltered = useMemo(
    () => (Object.keys(colFilters).length ? rows.filter((r) => passes(r)) : rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, colFilters, columns],
  );

  const searched = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return colFiltered;
    return colFiltered.filter((r) =>
      columns.some((c) => String(valueOf(r, c) ?? '').toLowerCase().includes(needle)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colFiltered, columns, q]);

  const sortedRaw = useMemo(() => {
    if (!sortKey) return searched;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return searched;
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...searched].sort((a, b) => {
      const av = valueOf(a, col);
      const bv = valueOf(b, col);
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1; // blanks always last
      if (bEmpty) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mul;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, sortKey, sortDir, columns]);
  const sorted = useMemo(() => {
    if (!pinTop) return sortedRaw;
    const top = sortedRaw.filter(pinTop);
    return top.length ? [...top, ...sortedRaw.filter((r) => !pinTop(r))] : sortedRaw;
  }, [sortedRaw, pinTop]);
  useEffect(() => { onFilteredRowsChange?.(searched); }, [searched, onFilteredRowsChange]);
  const activeFilters = Object.keys(colFilters).length;

  /**
   * Columns that say nothing about THESE rows.
   *
   * Judged on the filtered set rather than the visible page, so paging never
   * changes which columns exist. Deliberately conservative:
   *   - needs at least 3 rows, or "they are all the same" is meaningless
   *   - only columns with a `value` accessor, since that is the only thing we
   *     can compare; an Actions column of buttons is never touched
   *   - never the first column, which is the row's identity
   *   - never a totalled column, whose footer is the point
   */
  const deadKeys = useMemo(() => {
    if (!hideDeadColumns || sorted.length < 3) return new Set<string>();
    const blank = (v: unknown) => {
      const t = String(v ?? '').trim();
      return t === '' || t === '—' || t === '-' || t === 'N/A' || t === 'NA';
    };
    const dead = new Set<string>();
    columns.forEach((c, i) => {
      if (i === 0 || !c.value) return;
      if (totalKeys?.includes(c.key)) return;
      let first: string | null = null;
      let allBlank = true;
      let allSame = true;
      for (const r of sorted) {
        const raw = valueOf(r, c);
        if (!blank(raw)) allBlank = false;
        const norm = String(raw ?? '').trim();
        if (first === null) first = norm;
        else if (norm !== first) { allSame = false; break; }
      }
      if (allBlank || allSame) dead.add(c.key);
    });

    // Mirror columns: two columns showing the SAME value on every row are one
    // column's worth of information taking two columns of space. The finance
    // ledger showed Requested and Balance side by side, identical on all 36
    // rows, because nothing had been paid yet. The earlier column wins — it is
    // the one the reader reaches first.
    const live = columns.filter((c, i) => i > 0 && c.value && !dead.has(c.key));
    for (let a = 0; a < live.length; a++) {
      for (let b = a + 1; b < live.length; b++) {
        if (dead.has(live[b].key)) continue;
        if (totalKeys?.includes(live[b].key)) continue;
        let same = true;
        for (const r of sorted) {
          if (String(valueOf(r, live[a]) ?? '').trim() !== String(valueOf(r, live[b]) ?? '').trim()) { same = false; break; }
        }
        if (same) dead.add(live[b].key);
      }
    }
    return dead;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, columns, hideDeadColumns, totalKeys]);

  // What the table actually draws. Export and search still use every column.
  const shownColumns = useMemo(
    () => (showDead || !deadKeys.size ? columns : columns.filter((c) => !deadKeys.has(c.key))),
    [columns, deadKeys, showDead],
  );

  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount - 1);
  const visible = pageSize > 0 ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted;

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const doExport = () => {
    const cols = columns.filter((c) => !c.noExport);
    downloadCsv(
      exportName || 'export',
      cols.map((c) => c.header),
      sorted.map((r) => cols.map((c) => valueOf(r, c) ?? '')),
    );
  };

  const pad = dense ? 'px-2 py-1' : 'px-3 py-2';

  return (
    <Reveal className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
      {(searchable || toolbar || exportName) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/70 px-3 py-2">
          {searchable && (
            <div className="relative w-full sm:w-auto">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
                placeholder={searchPlaceholder}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-7 pr-2 text-xs outline-none focus:border-brand-400 sm:h-8 sm:w-56"
              />
            </div>
          )}
          {toolbar}
          <div className="flex items-center gap-2 text-[11px] text-slate-500 sm:ml-auto">
            {activeFilters > 0 && (
              <button
                onClick={() => { setColFilters({}); setPage(0); }}
                className="inline-flex items-center gap-1 rounded-lg border border-brand-300 bg-brand-50 px-2 py-1 font-medium text-brand-700 hover:bg-brand-100"
              >
                <Filter className="h-3 w-3" /> Clear {activeFilters} column filter{activeFilters === 1 ? '' : 's'}
              </button>
            )}
            {/* Folded columns are announced, never silently dropped — and one
                click brings them back. */}
            {deadKeys.size > 0 && (
              <button
                onClick={() => setShowDead((v) => !v)}
                title={
                  showDead
                    ? 'Hide columns that are identical on every row'
                    : `Same value on all ${sorted.length} rows: ${columns.filter((c) => deadKeys.has(c.key)).map((c) => c.header).join(', ')}`
                }
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 font-medium text-slate-600 hover:bg-slate-50"
              >
                {showDead
                  ? 'Hide repeated columns'
                  : `${deadKeys.size} repeated column${deadKeys.size === 1 ? '' : 's'} hidden`}
              </button>
            )}
            <span>
              {sorted.length.toLocaleString('en-IN')}
              {sorted.length !== rows.length && ` of ${rows.length.toLocaleString('en-IN')}`} rows
            </span>
            {exportName && (
              <button
                onClick={doExport}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 font-medium text-slate-600 hover:bg-slate-50"
              >
                <Download className="h-3 w-3" /> Excel
              </button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
        <table className="w-full min-w-full border-collapse text-xs">
          <thead>
            <tr className="sticky top-0 z-10">
              {expand && <th className="w-px border-b border-r border-slate-300 bg-slate-100 px-2 py-2" />}
              {shownColumns.map((c) => {
                const active = sortKey === c.key;
                const sortable = c.sortable !== false && !!c.value;
                return (
                  <th
                    key={c.key}
                    onClick={sortable ? () => toggleSort(c.key) : undefined}
                    className={[
                      'border-b border-r border-slate-300 bg-slate-100 font-semibold uppercase tracking-wide text-[10px] text-slate-600 last:border-r-0',
                      pad,
                      c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left',
                      c.grow ? '' : 'w-px whitespace-nowrap',
                      sortable ? 'cursor-pointer select-none hover:bg-slate-200' : '',
                    ].join(' ')}
                  >
                    <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {c.header}
                      {active &&
                        (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                      {columnFilters && c.value && c.filterable !== false && (
                        <button
                          type="button"
                          title={colFilters[c.key] ? `Filtered: ${colFilters[c.key].size} value(s)` : 'Filter'}
                          onClick={(e) => {
                            e.stopPropagation();
                            const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setFilterOpen(filterOpen?.key === c.key ? null : { key: c.key, x: Math.min(b.left, window.innerWidth - 270), y: b.bottom + 4 });
                          }}
                          className={`rounded p-0.5 ${colFilters[c.key] ? 'bg-brand-600 text-white' : 'text-slate-400 hover:bg-slate-300 hover:text-slate-700'}`}
                        >
                          <Filter className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td
                  colSpan={shownColumns.length + (expand ? 1 : 0)}
                  className="border-b border-slate-200 px-3 py-10 text-center text-slate-400"
                >
                  {emptyText}
                </td>
              </tr>
            )}
            {visible.map((row, i) => {
              const key = rowKey(row, i);
              const isOpen = open === key;
              const clickable = !!expand || !!onRowClick;
              return (
                <Fragment key={key}>
                  <tr
                    onClick={
                      clickable
                        ? () => {
                            if (onRowClick) onRowClick(row);
                            else setOpen(isOpen ? null : key);
                          }
                        : undefined
                    }
                    className={[
                      'border-b border-slate-200 even:bg-slate-50/60',
                      clickable ? 'cursor-pointer hover:bg-brand-50' : '',
                      isOpen ? 'bg-brand-50' : '',
                    ].join(' ')}
                  >
                    {expand && (
                      <td
                        className="w-px border-r border-slate-200 px-2 py-1 text-slate-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(isOpen ? null : key);
                        }}
                      >
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </td>
                    )}
                    {shownColumns.map((c) => (
                      <td
                        key={c.key}
                        className={[
                          'border-r border-slate-200 text-slate-700 last:border-r-0',
                          pad,
                          c.align === 'right' ? 'text-right tabular-nums' : c.align === 'center' ? 'text-center' : '',
                          c.grow ? '' : 'w-px whitespace-nowrap',
                          c.cellClass?.(row) ?? '',
                        ].join(' ')}
                      >
                        {c.render ? c.render(row) : (valueOf(row, c) ?? '—')}
                      </td>
                    ))}
                  </tr>
                  {expand && isOpen && (
                    <tr className="border-b-2 border-brand-200 bg-brand-50/40">
                      <td colSpan={shownColumns.length + 1} className="px-4 py-3">
                        {expand(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          {!!totalKeys?.length && sorted.length > 0 && (
            <tfoot className="sticky bottom-0 z-10">
              <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold text-slate-800">
                {expand && <td className="w-px border-r border-slate-200" />}
                {shownColumns.map((c, i) => {
                  if (!totalKeys.includes(c.key)) {
                    return (
                      <td key={c.key} className={`border-r border-slate-200 last:border-r-0 ${pad} ${c.grow ? '' : 'w-px whitespace-nowrap'}`}>
                        {i === 0 ? `Total · ${sorted.length.toLocaleString('en-IN')} row${sorted.length === 1 ? '' : 's'}` : ''}
                      </td>
                    );
                  }
                  const sum = sorted.reduce((a, r) => a + (Number(valueOf(r, c)) || 0), 0);
                  return (
                    <td key={c.key}
                      className={`border-r border-slate-200 tabular-nums last:border-r-0 ${pad} ${c.align === 'right' ? 'text-right' : ''} ${c.grow ? '' : 'w-px whitespace-nowrap'}`}>
                      {formatTotal ? formatTotal(c.key, sum) : sum.toLocaleString('en-IN')}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {(pageCount > 1 || caption) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-500">
          {caption}
          {pageCount > 1 && (
            <div className="ml-auto flex items-center gap-1">
              <button
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
                className="rounded border border-slate-200 bg-white px-2 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <span className="px-1">
                Page {safePage + 1} of {pageCount}
              </span>
              <button
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
                className="rounded border border-slate-200 bg-white px-2 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      {filterOpen && (() => {
        const col = columns.find((c) => c.key === filterOpen.key);
        if (!col) return null;
        // Cascading, like Excel: values left by the OTHER columns' filters.
        const counts = new Map<string, number>();
        for (const r of rows) {
          if (!passes(r, col.key)) continue;
          const v = filterLabel(valueOf(r, col));
          counts.set(v, (counts.get(v) || 0) + 1);
        }
        const values = Array.from(counts.keys()).sort((a, b) =>
          a === BLANK ? 1 : b === BLANK ? -1 : a.localeCompare(b, undefined, { numeric: true }));
        return (
          <ColumnFilterMenu
            key={col.key}
            title={col.header}
            values={values}
            counts={counts}
            selected={colFilters[col.key]}
            x={filterOpen.x}
            y={filterOpen.y}
            onClose={() => setFilterOpen(null)}
            onApply={(sel) => {
              setColFilters((prev) => {
                const next = { ...prev };
                if (!sel || sel.size === values.length) delete next[col.key];
                else next[col.key] = sel;
                return next;
              });
              setPage(0);
              setFilterOpen(null);
            }}
          />
        );
      })()}
    </Reveal>
  );
}

/** The Excel-style value picker under a column header. */
function ColumnFilterMenu({ title, values, counts, selected, x, y, onClose, onApply }: {
  title: string; values: string[]; counts: Map<string, number>; selected?: Set<string>;
  x: number; y: number; onClose: () => void; onApply: (sel: Set<string> | null) => void;
}) {
  const [draft, setDraft] = useState<Set<string>>(() => new Set(selected ? values.filter((v) => selected.has(v)) : values));
  const [find, setFind] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // The menu is pinned to where the header was; scrolling the page (but not
    // the menu's own list) moves the header away, so close it like Excel does.
    const scroll = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [onClose]);
  const shown = find.trim() ? values.filter((v) => v.toLowerCase().includes(find.trim().toLowerCase())) : values;
  const allShown = shown.length > 0 && shown.every((v) => draft.has(v));
  const toggle = (v: string) => setDraft((d) => { const n = new Set(d); if (n.has(v)) n.delete(v); else n.add(v); return n; });
  // Portalled to <body>: the table sits inside an animated (transformed)
  // wrapper, and position:fixed inside a transform is relative to that wrapper,
  // which dropped the menu far below its header.
  return createPortal(
    <div
      ref={ref}
      style={{ position: 'fixed', left: Math.max(8, x), top: Math.min(y, window.innerHeight - 380), zIndex: 60 }}
      className="w-64 rounded-lg border border-slate-200 bg-white p-2 text-xs normal-case tracking-normal shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-1 font-semibold text-slate-700">{title}</div>
      <input
        autoFocus
        value={find}
        onChange={(e) => setFind(e.target.value)}
        placeholder="Search values…"
        className="mb-1.5 h-7 w-full rounded border border-slate-200 px-2 outline-none focus:border-brand-400"
      />
      <label className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-1 py-1 font-medium">
        <input
          type="checkbox"
          checked={allShown}
          onChange={() => setDraft((d) => { const n = new Set(d); shown.forEach((v) => (allShown ? n.delete(v) : n.add(v))); return n; })}
        />
        (Select all{find.trim() ? ' shown' : ''})
      </label>
      <div className="max-h-56 overflow-y-auto py-1">
        {shown.map((v) => (
          <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-slate-50">
            <input type="checkbox" checked={draft.has(v)} onChange={() => toggle(v)} />
            <span className={`flex-1 truncate ${v === BLANK ? 'italic text-slate-400' : ''}`} title={v}>{v}</span>
            <span className="text-[10px] text-slate-400">{counts.get(v)}</span>
          </label>
        ))}
        {shown.length === 0 && <div className="px-1 py-2 text-slate-400">No matching values</div>}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 border-t border-slate-100 pt-1.5">
        <button className="text-slate-500 hover:text-slate-800" onClick={() => onApply(null)}>Clear</button>
        <div className="flex gap-1.5">
          <button className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50" onClick={onClose}>Cancel</button>
          <button className="rounded bg-brand-600 px-2 py-1 font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
            disabled={draft.size === 0} onClick={() => onApply(draft)}>OK</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
